import { createSelector } from 'reselect';
import moment from 'moment';

import { AMORTIZATION_TYPES_DICT, SCHEDULE_CHART_LINES } from './constants';
import { INTL_IDS } from './messages';
import { initialState } from './reducer';
import {
  convertNumToCurrencyStr,
  formatDate,
  formatErrorMessages,
  formatPercentage,
} from '../../../utils/globalHelpers';
import makeSelectMobile from '../../Mobile/selectors';
import {
  selectAllowedSetting as selectAutopayAllowedSetting,
  selectAutopayFlow,
  selectStatusDataProp as selectAutopayStatusDataProp,
} from '../../Payments/Autopay/selectors';
import { getNextPaymentDueDetails } from '../../Main/selectors';
import {
  selectIsAllowedToScheduleOTP,
  selectOtp,
  selectOtpForm,
} from '../../Payments/OneTimePayment/selectors';
import { formatScheduledPayments, generateChartData } from './helpers';

/**
 * Direct selector to the Amortize state domain
 */
const selectAmortizeDomain = (state) => state.amortize || initialState;

/**
 * Direct selector to the intl object
 */
const selectIntl = (_, props) => props.intl;

const selectAutopayAmount = () => createSelector(
  selectForm(),
  ({ amount }) => ({ amount: { additionalPrincipal: Number(amount) } }),
);

const selectCalculatedData = () => createSelector(
  makeSelectAmortize('currentPaymentsData'),
  selectStatusDataPaymentProp(),
  ({ cumulativeInterestAmount, paymentDate }, payments) => {
    if (!payments || payments.length === 0) {
      return {
        interestSavings: convertNumToCurrencyStr(0, 0),
        payoffDate: formatDate(paymentDate, 'UTC', 'MMM YYYY'),
        shortenLoanBy: '0 years',
      };
    }
    const {
      cumulativeInterestAmount: calculatedInterestAmount,
      paymentDate: calculatedPaymentDate,
    } = payments[payments.length - 1];
    let shortenLoanBy;
    const monthsDiff = moment(paymentDate).diff(moment(calculatedPaymentDate), 'months');
    if (monthsDiff < 12) {
      shortenLoanBy = `${monthsDiff} month${monthsDiff !== 1 ? 's' : ''}`;
    } else {
      shortenLoanBy = monthsDiff % 12 === 0 ?
        `${monthsDiff / 12} year${monthsDiff !== 12 ? 's' : ''}` :
        `${(monthsDiff / 12).toFixed(1)} years`;
    }
    return {
      interestSavings: convertNumToCurrencyStr(
        cumulativeInterestAmount - calculatedInterestAmount, 0,
      ),
      payoffDate: formatDate(calculatedPaymentDate, 'UTC', 'MMM YYYY'),
      shortenLoanBy,
    };
  },
);

const selectCalcProps = (state, props) => props;

const selectCalculateRequestBody = () => createSelector(
  makeSelectAmortize('selectedAdjustmentFilter'),
  selectForm('amortization'),
  selectFormValues(),
  selectStatusDataProp('currentSetting'),
  (
    adjustmentFilter,
    amortizationForm,
    { amount, startDate },
    currentSetting,
  ) => {
    if (currentSetting) {
      if (adjustmentFilter === 'amortization') {
        const {
          amount: additionalAmount,
          interestRate,
          monthlyPaymentAmount,
          numberOfPayments,
          paymentType,
          principalBalance,
          startDate: additionalStartDate,
          valueType,
        } = amortizationForm;
        const value = valueType === 'Monthly Payment Amount' ?
          monthlyPaymentAmount : numberOfPayments;
        const additionalPayment = paymentType !== 'Optional' ? {
          Additional_Payment: {
            Payment_Amount: Number(additionalAmount),
            Payment_Type: AMORTIZATION_TYPES_DICT[paymentType],
            Start_Date: additionalStartDate,
          },
        } : {};
        const interestRateToUse = (Number(interestRate) / 100).toFixed(5);

        return {
          Input: { Value: Number(value), Value_Type: AMORTIZATION_TYPES_DICT[valueType] },
          Interest_Rate: Number(interestRateToUse),
          Principal_Balance: Number(principalBalance),
          ...additionalPayment,
        };
      }

      const { interestRate, monthlyPaymentAmount, principalBalance } = currentSetting;
      const paymentType = adjustmentFilter === 'payMoreMonthly' ? 'Monthly' : 'One_Time';

      return {
        Additional_Payment: {
          Payment_Amount: amount,
          Payment_Type: paymentType,
          Start_Date: startDate,
        },
        Input: { Value: monthlyPaymentAmount, Value_Type: 'Monthly_Payment_Amount' },
        Interest_Rate: interestRate,
        Principal_Balance: principalBalance,
      };
    }
    return {};
  },
);

const selectCalendarProps = () => createSelector(
  selectStatusDataProp('currentSetting'),
  selectDefaultStartDate(),
  ({ maximumStartDateAdditionalPayment }, defaultStartDate) => ({
    maxDate: moment(maximumStartDateAdditionalPayment).format('YYYY-MM-DD'),
    minDate: moment(defaultStartDate).format('YYYY-MM-DD'),
  }),
);

const selectChartDataToDisplay = () => createSelector(
  makeSelectAmortize('statusData'),
  ({ defaultPayments, newPayments }) => {
    const response = {};
    if (defaultPayments) {
      response.initialScheduleData = generateChartData({
        data: defaultPayments,
        linesToChart: SCHEDULE_CHART_LINES,
      });
    }
    if (newPayments) {
      response.newScheduleData = generateChartData({
        data: newPayments,
        linesToChart: SCHEDULE_CHART_LINES,
      });
    }
    return response;
  },
);

const selectDefaultStartDate = () => createSelector(
  selectStatusDataProp('currentSetting'),
  (currentSetting) => {
    const { minimumStartDateAdditionalPayment } = currentSetting || {};
    const today = `${moment().format('YYYY-MM-DD')}T00:00:00`;
    return moment(minimumStartDateAdditionalPayment).isAfter(today) ?
      minimumStartDateAdditionalPayment :
      today;
  },
);

const selectErrors = (prop) => createSelector(
  makeSelectAmortize('errors'),
  (errors) => errors[prop],
);

const selectForm = (prop) => createSelector(
  makeSelectAmortize('selectedAdjustmentFilter'),
  makeSelectAmortize('forms'),
  (adjustmentFilter, forms) => forms[prop || adjustmentFilter],
);

const selectFormattedFormValues = () => createSelector(
  selectFormValues(),
  (formValues) => Object.entries(formValues).reduce((acc, [field, value]) => {
    if (field === 'startDate' && value) acc[field] = formatDate(value, 'UTC', 'LL');
    const digits = Number(value) % 1 !== 0 ? 2 : 0;
    if (field === 'amount' && value) acc[field] = convertNumToCurrencyStr(Number(value), digits);
    return acc;
  }, {}),
);

const selectFormErrors = (prop) => createSelector(
  makeSelectAmortize('selectedAdjustmentFilter'),
  makeSelectAmortize('formErrors'),
  (adjustmentFilter, formErrors) => formErrors[prop || adjustmentFilter],
);

const selectFormValues = () => createSelector(
  makeSelectAmortize('selectedAdjustmentFilter'),
  makeSelectAmortize('forms'),
  selectDefaultStartDate(),
  (adjustmentFilter, forms, defaultStartDate) => {
    switch (adjustmentFilter) {
      case 'amortization': {
        const { startDate, ...restValues } = forms[adjustmentFilter];
        return {
          startDate: startDate || defaultStartDate,
          ...restValues,
        };
      }
      case 'payLumpSum': {
        const { amount, resultAmount } = forms[adjustmentFilter];
        return {
          amount,
          resultAmount,
          startDate: defaultStartDate,
        };
      }
      case 'payMoreMonthly': {
        const { amount, resultAmount } = forms[adjustmentFilter];
        return {
          amount,
          resultAmount,
          startDate: defaultStartDate,
        };
      }
      default:
        return {};
    }
  },
);

const selectInitialTerms = () => createSelector(
  selectStatusDataProp('currentSetting'),
  selectIntl,
  (currentSetting, intl) => {
    if (currentSetting) {
      const {
        interestRate,
        monthlyPaymentAmount,
        principalBalance,
        remainingNumberOfPayments,
      } = currentSetting;
      return [
        {
          label: intl.formatMessage({ id: INTL_IDS.INTIALTERMS_LABEL_LOANAMOUNT }),
          value: convertNumToCurrencyStr(principalBalance),
        },
        {
          label: intl.formatMessage({ id: INTL_IDS.INTIALTERMS_LABEL_INTERESTRATE }),
          value: formatPercentage(interestRate),
        },
        {
          label: intl.formatMessage({ id: INTL_IDS.INTIALTERMS_LABEL_NUMBEROFMONTHS }),
          value: remainingNumberOfPayments,
        },
        {
          label: intl.formatMessage({ id: INTL_IDS.INTIALTERMS_LABEL_MONTHLYPAYMENT }),
          value: convertNumToCurrencyStr(monthlyPaymentAmount),
        },
      ];
    }
    return [];
  },
);

const selectIsAmortizationScheduleCalculated = () => createSelector(
  selectStatusDataPaymentProp(),
  (payments) => !!payments,
);

const selectIsAutopaySetUp = () => createSelector(
  selectAutopayStatusDataProp('status'),
  (autopayStatus) => autopayStatus === 'Enabled',
);

const selectIsCalculateButtonDisabled = () => createSelector(
  selectForm('amortization'),
  selectFormErrors('amortization'),
  (values, errors) => {
    const {
      amount,
      interestRate,
      monthlyPaymentAmount,
      numberOfPayments,
      paymentType,
      principalBalance,
      startDate,
      valueType,
    } = values;
    const {
      amount: amountError,
      interestRate: interestRateError,
      monthlyPaymentAmount: monthlyPaymentAmountError,
      numberOfPayments: numberOfPaymentsError,
      principalBalance: principalBalanceError,
      startDate: startDateError,
    } = errors;
    const isValues = !!(
      interestRate &&
      (monthlyPaymentAmount || numberOfPayments) &&
      principalBalance &&
      valueType &&
      (paymentType !== 'Optional' ? startDate && amount : true)
    );
    const isErrors = !!(
      interestRateError || principalBalanceError ||
      (valueType === 'Monthly Payment Amount' ? monthlyPaymentAmountError : numberOfPaymentsError)
      || (paymentType !== 'Optional' ? (amountError || startDateError) : false)
    );

    return !isValues || isErrors;
  },
);

const selectIsCalculatorDataValid = () => createSelector(
  selectStatusDataProp('currentSetting'),
  ({ remainingNumberOfPayments }) => !!remainingNumberOfPayments,
);

const selectIsFormValidated = () => createSelector(
  selectFormErrors(),
  (formErrors) => !formErrors.amount,
);

const selectMaximumPaymentAmount = () => createSelector(
  makeSelectAmortize('selectedAdjustmentFilter'),
  selectStatusDataProp('currentSetting'),
  selectIsAllowedToScheduleOTP(),
  selectAutopayFlow(),
  selectAutopayAllowedSetting('maximumAdditionalPrincipal'),
  selectOtp('maximumAllowedAdditionalPrincipal'),
  getNextPaymentDueDetails(),
  selectOtp('paymentLogic'),
  (
    adjustmentFilter,
    { principalBalance },
    isAllowedToScheduleOTP,
    autopayFlow,
    maxAutopayAdditionalPrincipal,
    maxOtpAdditionalPrincipal,
    { regularMonthlyPayment },
    { canMakeAdditionalPayment },
  ) => {
    if (adjustmentFilter === 'payMoreMonthly') {
      return ['change', 'enable'].includes(autopayFlow) ?
        maxAutopayAdditionalPrincipal :
        principalBalance;
    }
    return isAllowedToScheduleOTP ?
      maxOtpAdditionalPrincipal - (canMakeAdditionalPayment ? 0 : regularMonthlyPayment) :
      principalBalance;
  },
);

const selectMenuItems = () => createSelector(
  selectStatusDataProp('currentSetting'),
  makeSelectMobile('isMobile'),
  (currentSetting, isMobile) => {
    if (currentSetting) {
      const { allowedAdditionalPaymentTypes, allowedInputValueTypes } = currentSetting;
      const labelKey = isMobile ? 'label' : 'display';
      const valueTypes = allowedInputValueTypes.map((type) => (
        { [labelKey]: AMORTIZATION_TYPES_DICT[type], value: AMORTIZATION_TYPES_DICT[type] }
      ));
      const paymentTypes = allowedAdditionalPaymentTypes.map((type) => (
        { [labelKey]: AMORTIZATION_TYPES_DICT[type], value: AMORTIZATION_TYPES_DICT[type] }
      ));
      paymentTypes.unshift({ [labelKey]: 'Optional', value: 'Optional' });
      return { paymentTypes, valueTypes };
    }
    return {};
  },
);

const selectNotifications = () => createSelector(
  selectFormErrors('bannerError'),
  selectFormErrors('serviceError'),
  selectFormErrors(),
  (bannerError, serviceError, formErrors) => {
    const nonFormErrors = [bannerError, serviceError];
    const notifications = nonFormErrors.filter((error) => !!error);
    return notifications.concat(formatErrorMessages({ ...formErrors }));
  },
);

const selectOtpAmount = () => createSelector(
  selectFormValues(),
  selectOtpForm('amount'),
  selectOtp('paymentLogic'),
  ({ amount }, otpAmountForm, { canMakeAdditionalPayment }) => ({
    amount: { ...otpAmountForm, additionalPrincipal: Number(amount) },
    payType: canMakeAdditionalPayment ? 'Additional' : 'Monthly',
  }),
);

const selectPageView = () => createSelector(
  selectCalcProps,
  (props) => props.match.params.view || 'main',
);

const selectProceedSummaryBarProps = () => createSelector(
  makeSelectAmortize('selectedAdjustmentFilter'),
  selectFormattedFormValues(),
  selectUpdatedMonthlyPayment(),
  selectIsAutopaySetUp(),
  (adjustmentFilter, formattedFormValues, updatedMonthlyPayment, isAutopaySetUp) => {
    switch (adjustmentFilter) {
      case 'payLumpSum': {
        const { amount } = formattedFormValues;
        return {
          isButtonDisabled: false,
          summaryLabel: 'Next',
          summarySubText: 'Lump Sum Payment',
          summaryText: amount,
        };
      }
      case 'payMoreMonthly': {
        const { amount } = formattedFormValues;
        return {
          isButtonDisabled: false,
          summaryLabel: 'Next',
          summarySubText: isAutopaySetUp ? 'Additional Monthly Payment' : 'Updated Monthly Payment',
          summaryText: isAutopaySetUp ? amount : updatedMonthlyPayment,
        };
      }
      default:
        return {};
    }
  },
);

const selectResultsData = () => createSelector(
  makeSelectAmortize('selectedAdjustmentFilter'),
  selectCalculatedData(),
  selectIntl,
  (
    adjustmentFilter,
    { interestSavings, payoffDate, shortenLoanBy },
    intl,
  ) => {
    switch (adjustmentFilter) {
      case 'payLumpSum':
        return [
          {
            label: intl.formatMessage(
              { id: INTL_IDS.RESULTSDATA_LABEL_PAYOFFDATE },
            ),
            value: payoffDate,
          },
          {
            label: intl.formatMessage(
              { id: INTL_IDS.RESULTSDATA_LABEL_SHORTENLOANBY },
            ),
            value: shortenLoanBy,
          },
          {
            label: intl.formatMessage(
              { id: INTL_IDS.RESULTSDATA_LABEL_INTERESTSAVINGS },
            ),
            value: interestSavings,
          },
        ];
      case 'payMoreMonthly':
        return [
          {
            label: intl.formatMessage(
              { id: INTL_IDS.RESULTSDATA_LABEL_PAYOFFDATE },
            ),
            value: payoffDate,
          },
          {
            label: intl.formatMessage(
              { id: INTL_IDS.RESULTSDATA_LABEL_SHORTENLOANBY },
            ),
            value: shortenLoanBy,
          },
          {
            label: intl.formatMessage(
              { id: INTL_IDS.RESULTSDATA_LABEL_INTERESTSAVINGS },
            ),
            value: interestSavings,
          },
        ];
      default:
        return [];
    }
  },
);

const selectResultsSummaryBarProps = () => createSelector(
  makeSelectAmortize('selectedAdjustmentFilter'),
  selectIsAmortizationScheduleCalculated(),
  selectFormattedFormValues(),
  selectIsAllowedToScheduleOTP(),
  selectAutopayFlow(),
  selectIsFormValidated(),
  makeSelectMobile('isMobile'),
  (
    adjustmentFilter,
    isAmortizationScheduleCalculated,
    formattedFormValues,
    isAllowedToScheduleOTP,
    autopayFlow,
    isFormValidated,
    isMobile,
  ) => {
    switch (adjustmentFilter) {
      case 'payLumpSum': {
        return {
          isButtonDisabled:
            !isAllowedToScheduleOTP ||
            !isAmortizationScheduleCalculated ||
            !isFormValidated,
          summaryLabel: isMobile ? 'Make this Payment' : 'Make this Lump Sum Payment',
        };
      }
      case 'payMoreMonthly': {
        return {
          isButtonDisabled:
            !['change', 'enable'].includes(autopayFlow) ||
            !isAmortizationScheduleCalculated ||
            !isFormValidated,
          summaryLabel: 'Update Monthly Payment',
        };
      }
      default:
        return {};
    }
  },
);

const selectScheduleToDisplay = () => createSelector(
  makeSelectAmortize('statusData'),
  selectIsAmortizationScheduleCalculated(),
  selectStatusDataPaymentProp(),
  ({ defaultPayments }, isAmortizationScheduleCalculated, payments) => {
    if (defaultPayments || payments) {
      const paymentsToUse = isAmortizationScheduleCalculated ? payments : defaultPayments;
      const formattedScheduledPayments =
        paymentsToUse.map((payment) => formatScheduledPayments(payment));
      return formattedScheduledPayments;
    }
    return [];
  },
);

const selectServiceErrorMessage = (prop) => createSelector(
  selectErrors('serviceError'),
  (serviceError) => serviceError[prop] || '',
);

const selectStatusDataProp = (prop) => createSelector(
  makeSelectAmortize('statusData'),
  (statusData) => statusData[prop],
);

const selectStatusDataPaymentProp = () => createSelector(
  makeSelectAmortize('selectedAdjustmentFilter'),
  makeSelectAmortize('statusData'),
  (adjustmentFilter, statusData) => {
    if (adjustmentFilter === 'amortization') {
      return statusData.newPayments;
    }
    return statusData.payments[adjustmentFilter];
  },
);

const selectUpdatedMonthlyPayment = () => createSelector(
  selectFormValues(),
  selectAutopayStatusDataProp('regularMonthlyPayment'),
  (
    { amount },
    regularMonthlyPayment = 0,
  ) => convertNumToCurrencyStr(regularMonthlyPayment + Number(amount)),
);

const selectIsSliderFocused = () => createSelector(
  makeSelectAmortize('isSliderFocused'),
  makeSelectAmortize('selectedAdjustmentFilter'),
  (isSliderFocused, adjustmentFilter) => isSliderFocused[adjustmentFilter],
);

/**
 * Default selector used by Amortize
 */
const makeSelectAmortize = (prop) => createSelector(
  selectAmortizeDomain,
  (state) => state[prop],
);

export default selectAmortizeDomain;
export {
  makeSelectAmortize,
  selectAutopayAmount,
  selectCalculateRequestBody,
  selectCalendarProps,
  selectChartDataToDisplay,
  selectErrors,
  selectForm,
  selectFormattedFormValues,
  selectFormErrors,
  selectFormValues,
  selectInitialTerms,
  selectIsAmortizationScheduleCalculated,
  selectIsAutopaySetUp,
  selectIsCalculateButtonDisabled,
  selectIsCalculatorDataValid,
  selectIsFormValidated,
  selectIsSliderFocused,
  selectMaximumPaymentAmount,
  selectMenuItems,
  selectNotifications,
  selectOtpAmount,
  selectPageView,
  selectProceedSummaryBarProps,
  selectResultsData,
  selectResultsSummaryBarProps,
  selectScheduleToDisplay,
  selectServiceErrorMessage,
  selectStatusDataProp,
};
